import Flex from "../../components/Flex";
import Text from "../../components/Text";
import { NonNullableDate } from "../../utils/date-time-utility";
import { replaceUnderScoreAddSpace } from "../../utils/utils";
import { ListContent, ReportContainer } from "../constant";

interface ScreeningAssessmentBlockProps {
  data: any;
}

/**
 * Severity reaches the report under two different keys depending on which
 * backend payload the report was built from: the screener answer response uses
 * `severity` (NONE_MINIMAL … SEVERE) while the encounter snapshot carries the
 * questionnaire's own `severityRange` (VERY_LOW … VERY_HIGH). Both are read so
 * the line matches the encounter screen instead of falling back to "-".
 */
const getSeverity = (result: any) => result?.severity ?? result?.severityRange;

/**
 * Builds the same one-liner the encounter screen prints for a screener
 * (ScreenerPreview.tsx) — "AAS - Score - 3 - None Minimal (07/30/2026)".
 * Segments the payload did not carry are dropped rather than printed as "-".
 */
const buildScreenerLine = (screenerType: string, result: any) => {
  const severity = getSeverity(result);
  const createdAt = result?.createdAt;

  const line = [
    replaceUnderScoreAddSpace(screenerType),
    result?.score !== undefined && result?.score !== null
      ? `Score - ${result?.score}`
      : null,
    severity ? replaceUnderScoreAddSpace(String(severity).toLowerCase()) : null,
  ]
    .filter(Boolean)
    .join(" - ");

  return createdAt ? `${line} (${NonNullableDate(createdAt)})` : line;
};

/**
 * Renders the screening assessments completed during the visit.
 *
 * `patientQuestionAnswers` arrives as a map of screener type -> result, e.g.
 * `{ PHQ_9: { score, severity, screenerQuestionAnwerResponseSet } }`. Only the
 * headline values (assessment name, score and severity) are printed — the
 * per-question answer set is deliberately left out because a full screener runs
 * to dozens of rows and would dwarf the rest of the report.
 */
const ScreeningAssessmentBlock = ({ data }: ScreeningAssessmentBlockProps) => {
  const screeners = Object.entries(
    (data?.patientQuestionAnswers ?? {}) as Record<string, any>
  );

  if (!screeners.length) return null;

  return (
    <ReportContainer label="Screening Assessments">
      <ListContent
        list={screeners}
        renderItem={([screenerType, result]: any, index) => (
          <Flex key={screenerType} gap={0.5}>
            <Text fontWeight={"bold"}>{index + 1}.</Text>
            <Text>{buildScreenerLine(screenerType, result)}</Text>
          </Flex>
        )}
      />
    </ReportContainer>
  );
};

export default ScreeningAssessmentBlock;
